/**
 * Transforms image URLs to point to the new S3 bucket
 * Backend now sends identifiers only, not Google Places URLs, so no URL conversion needed
 * Use this for all image URLs to ensure they point to the correct bucket
 */
export const transformImageUrl = (url: string | undefined | null): string | undefined | null => {
    if (!url) return url;
    if (typeof url !== 'string') return url;
    
    // Transform S3 bucket URLs to new bucket names
    return url
      .replace(
        'globethriversmedia112148-dev.s3.eu-west-1.amazonaws.com',
        'globethriversmedia07c55-devnew.s3.eu-west-1.amazonaws.com'
      )
      .replace(
        'globethriversmedia112710-prod.s3.eu-west-1.amazonaws.com',
        'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
      )
      .replace(
        'globethriversmedia112710-prod.s3-accelerate.amazonaws.com',
        'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
      );
  }
  
  /**
   * Transforms image URLs in an object that may contain nested objects or arrays
   * This is useful for transforming all image URLs in an API response
   */
  export const transformImageUrls = <T>(obj: T): T => {
    if (!obj) return obj;
    
    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => transformImageUrls(item)) as unknown as T;
    }
    
    // Handle objects
    if (typeof obj === 'object') {
      const result = { ...obj } as Record<string, unknown>;
      
      for (const key in result) {
        // Transform image URL fields
        if (
          (typeof result[key] === 'string') && 
          (
            key.includes('image') || 
            key.includes('avatar') || 
            key.includes('photo') || 
            key.includes('picture') ||
            key.includes('hero') ||
            key.includes('thumbnail') ||
            key.includes('icon')
          )
        ) {
          result[key] = transformImageUrl(result[key]);
        } 
        // Recursively transform nested objects and arrays
        else if (typeof result[key] === 'object' && result[key] !== null) {
          result[key] = transformImageUrls(result[key]);
        }
      }
      
      return result as T;
    }
    
    // Return primitives as is
    return obj;
  }

/**
 * Gets current S3 bucket name based on environment
 * Matches app behavior: uses S3_BUCKET env var, falls back to prod
 */
export function getS3BucketName(): string {
  // Note: This function can be called on both client and server
  // On client, we need to check if we're in browser (window exists)
  if (typeof window !== 'undefined') {
    // Client-side: S3_BUCKET would need to be NEXT_PUBLIC_S3_BUCKET to be accessible
    // For now, we'll use a runtime check or default to prod
    // In practice, bucket selection happens server-side in upload route
    return 'globethriversmedia87608-prodnew'; // Default fallback
  }
  // Server-side: can access process.env.S3_BUCKET
  return process.env.S3_BUCKET || 'globethriversmedia87608-prodnew';
}

/**
 * Determines if running in dev environment
 * Works on both client and server
 */
export function isDevEnvironment(): boolean {
  // On server, check S3_BUCKET env var
  if (typeof window === 'undefined') {
    const bucket = getS3BucketName();
    return bucket.includes('dev');
  }
  // On client, we can't access process.env.S3_BUCKET
  // Default to prod (safer) - the primary URL should already be correct
  // The fallback will try alternate bucket if primary fails
  return false;
}

/**
 * Determines which bucket a URL points to
 * @param url - S3 URL
 * @returns 'dev' | 'prod' | null if not an S3 URL
 */
export function getBucketFromUrl(url: string): 'dev' | 'prod' | null {
  if (!url || typeof url !== 'string') return null;
  if (url.includes('globethriversmedia07c55-devnew')) return 'dev';
  if (url.includes('globethriversmedia87608-prodnew')) return 'prod';
  return null;
}

/**
 * Toggles the public/ prefix in an S3 URL path
 * @param url - Original S3 URL
 * @param addPublic - If true, adds public/ prefix; if false, removes it
 * @returns Modified URL with toggled public/ prefix, or original URL if not an S3 URL
 */
export function togglePublicPrefix(url: string, addPublic: boolean): string {
  if (!url || typeof url !== 'string') return url;
  
  // Check if it's an S3 URL
  const s3Pattern = /https?:\/\/([^.]+)\.s3(?:\.([^.]+))?\.amazonaws\.com\/(.+)/;
  const match = url.match(s3Pattern);
  
  if (!match) return url;
  
  const [, bucket, region, key] = match;
  const regionPart = region ? `.${region}` : '';
  
  // Check if key already has public/ prefix
  const hasPublicPrefix = key.startsWith('public/');
  
  let newKey: string;
  if (addPublic && !hasPublicPrefix) {
    // Add public/ prefix
    newKey = `public/${key}`;
  } else if (!addPublic && hasPublicPrefix) {
    // Remove public/ prefix
    newKey = key.replace(/^public\//, '');
  } else {
    // No change needed
    return url;
  }
  
  return `https://${bucket}.s3${regionPart}.amazonaws.com/${newKey}`;
}

/**
 * Generates alternate bucket URL for cross-environment fallback
 * @param url - Original S3 URL
 * @param targetBucket - 'dev' or 'prod'
 * @returns Alternate bucket URL or empty string if not an S3 URL
 */
export function getAlternateBucketUrl(
  url: string, 
  targetBucket: 'dev' | 'prod'
): string {
  if (!url || typeof url !== 'string') return '';
  
  const devBucket = 'globethriversmedia07c55-devnew';
  const prodBucket = 'globethriversmedia87608-prodnew';
  const region = 'eu-west-1';
  
  // Check if it's an S3 URL (handle both with and without region in domain)
  // Pattern: https://bucket.s3.region.amazonaws.com/key or https://bucket.s3.amazonaws.com/key
  const s3Pattern = /https?:\/\/([^.]+)\.s3(?:\.([^.]+))?\.amazonaws\.com\/(.+)/;
  const match = url.match(s3Pattern);
  
  if (!match) return '';
  
  const [, , , key] = match;
  
  // Determine target bucket URL
  const targetBucketName = targetBucket === 'dev' ? devBucket : prodBucket;
  const targetRegion = region; // Both buckets are in eu-west-1
  
  return `https://${targetBucketName}.s3.${targetRegion}.amazonaws.com/${key}`;
} 